/**
 * shootingStar — occasional meteor streaks across the night sky. A small pool of
 * thin `ray_ribbon` streaks; each idles (alpha 0) until its own randomised timer
 * fires, then flies a short bright arc (travel along `angleDeg` + a sin fade) and
 * resets to wait again. Sparse + charming; a sky fixture (mooded:false).
 *
 * Same builder shape as the kit: narrow by kind, place from the seed, mint sprites
 * behind IRenderer (null-safe), drive them with ONE shared ctx.addTick, self-
 * register. REDUCE-MOTION: streaks ARE motion → when reduced we place none and
 * attach no tick (the sky just holds its stars). Pref read ONCE at build.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { seededRand } from '../atmospherePlacement';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const DEG2RAD = Math.PI / 180;

const buildShootingStar: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'shootingStar') return null;
  const reduceMotion = getMotionPrefs().reduceMotion;
  if (reduceMotion) return null; // pure motion — nothing to place statically

  const { region } = ctx;
  const spanX = region.x1 - region.x0;
  const spanY = region.y1 - region.y0;
  const dirX = Math.cos(layer.angleDeg * DEG2RAD);
  const dirY = Math.sin(layer.angleDeg * DEG2RAD);

  const sprites: ISprite[] = [];
  // Per-streak flight state: a thin `ray_ribbon` TAIL + a bright round HEAD that
  // leads it (sells the meteor read). `wait` counts down to the next launch; once
  // active, `t` runs 0→flightMs across the arc.
  const streaks: { tail: ISprite; head: ISprite; seed: number; active: boolean; wait: number; nextAt: number; t: number; flightMs: number; x0: number; y0: number; len: number; dist: number }[] = [];

  for (let i = 0; i < layer.count; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 7700 + i * 19.3;
    const tail = createGlowSprite({ x: -9999, y: -9999, size: 8, color: layer.color, alpha: 0, textureKey: 'ray_ribbon', blendMode: 'add', parent: ctx.layerContainer });
    if (!tail) break; // textures not ready — quieter sky, never a crash
    tail.setAngle(layer.angleDeg);
    const head = createGlowSprite({ x: -9999, y: -9999, size: Math.max(10, layer.thickness * 2.6), color: layer.color, alpha: 0, textureKey: 'glow_soft', blendMode: 'add', parent: ctx.layerContainer });
    if (!head) { tail.destroy(); break; }
    sprites.push(tail, head);
    streaks.push({ tail, head, seed, active: false, wait: 0, nextAt: lerp(layer.intervalMinMs, layer.intervalMaxMs, seededRand(seed)), t: 0, flightMs: 0, x0: 0, y0: 0, len: 0, dist: 0 });
  }
  if (!sprites.length) return null;

  let spawnTick = 0; // advances the per-streak seed so successive launches vary
  const launch = (st: typeof streaks[number]) => {
    spawnTick += 1;
    const r = (n: number) => seededRand(st.seed + spawnTick * 13.7 + n);
    // Start high in the sky, anywhere across the (camera-anchored) span.
    st.x0 = region.x0 + spanX * lerp(0.0, 0.9, r(1));
    st.y0 = region.y0 + spanY * lerp(0.02, 0.35, r(2));
    st.len = lerp(layer.lengthMin, layer.lengthMax, r(3));
    st.dist = spanX * lerp(0.35, 0.7, r(4)); // how far it travels before fading
    st.flightMs = (st.dist / Math.max(1, layer.speed)) * 1000;
    st.tail.setDisplaySize(st.len, layer.thickness);
    st.active = true;
    st.t = 0;
  };

  const unsub = ctx.addTick((dtMs: number) => {
    for (const st of streaks) {
      if (!st.active) {
        st.wait += dtMs;
        if (st.wait >= st.nextAt) { st.wait = 0; launch(st); }
        continue;
      }
      st.t += dtMs;
      const u = st.t / st.flightMs;
      if (u >= 1) {
        st.active = false;
        st.tail.setAlpha(0);
        st.head.setAlpha(0);
        st.nextAt = lerp(layer.intervalMinMs, layer.intervalMaxMs, seededRand(st.seed + spawnTick * 5.1));
        continue;
      }
      const cx = st.x0 + dirX * st.dist * u;
      const cy = st.y0 + dirY * st.dist * u;
      const env = Math.sin(u * Math.PI); // fade in then out across the arc
      st.tail.setPosition(cx, cy);
      st.tail.setAlpha(env * layer.alphaMax);
      // The bright head leads the tail by half its length along travel.
      st.head.setPosition(cx + dirX * st.len * 0.5, cy + dirY * st.len * 0.5);
      st.head.setAlpha(Math.min(1, env * layer.alphaMax * 1.6)); // brighter than the tail
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'shootingStar', build: buildShootingStar, depth: Z_LAYERS.CLOUDS, mooded: false });
